import { useEffect, useState, useCallback, useRef } from "react";

import { API_URL } from "@/lib/landing";
const STORAGE_KEY = "genie_waitlist_ref";

interface InviteCode {
  code: string;
  used: boolean;
  url: string;
}

interface WaitlistStatus {
  email: string;
  referralCode: string;
  position: number;
  effectivePosition: number;
  referralCount: number;
  inviteCodes: InviteCode[];
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function useCountUp(target: number, duration = 1000): number {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);
  const rafId = useRef<number>(0);

  useEffect(() => {
    if (target <= 0) { setValue(0); return; }
    startTime.current = null;

    function tick(now: number) {
      if (!startTime.current) startTime.current = now;
      const elapsed = now - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      }
    }

    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
  }, [target, duration]);

  return value;
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function InviteCodeRow({ invite, index }: { invite: InviteCode; index: number }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(invite.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  function handleShareX() {
    const text = `I just joined the waitlist for Genie — a creative intelligent canvas with 1000+ AI models. Join me:`;
    const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(invite.url)}`;
    window.open(intentUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className={`flex items-center gap-2 py-2.5 ${index > 0 ? "border-t border-[#f0f0f0]" : ""}`}>
      {/* Number */}
      <span className="w-5 text-xs text-[#bbb] font-medium shrink-0">{index + 1}</span>

      {/* Code + status */}
      <div className="flex-1 min-w-0">
        {invite.used ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-[#bbb] line-through">{invite.code}</span>
            <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">Used</span>
          </div>
        ) : (
          <span className="text-xs font-mono text-[#555] truncate block">{invite.url}</span>
        )}
      </div>

      {/* Actions */}
      {!invite.used && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-md hover:bg-[#f5f5f5] text-[#999] hover:text-[#111] transition-colors"
            aria-label={`Copy invite link ${index + 1}`}
          >
            {copied ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <CopyIcon />
            )}
          </button>
          <button
            onClick={handleShareX}
            className="p-1.5 rounded-md hover:bg-[#f5f5f5] text-[#999] hover:text-[#111] transition-colors"
            aria-label={`Share invite link ${index + 1} on X`}
          >
            <XIcon />
          </button>
        </div>
      )}
    </div>
  );
}

export function Waitlist() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<WaitlistStatus | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const animatedPosition = useCountUp(status?.effectivePosition ?? 0);
  const [referredByCode, setReferredByCode] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchStatus = useCallback(async (code: string) => {
    try {
      const res = await fetch(`${API_URL}/api/waitlist/status/${code}`);
      if (!res.ok) return null;
      const json = await res.json();
      if (!json.success) return null;
      return { ...json.data, referralCode: code } as WaitlistStatus;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const refParam = params.get("ref");
      if (refParam) {
        setReferredByCode(refParam);
      }
    }

    let storedCode: string | null = null;
    try {
      storedCode = localStorage.getItem(STORAGE_KEY);
    } catch {}

    if (storedCode) {
      fetchStatus(storedCode).then((result) => {
        if (result) setStatus(result);
        setInitialLoading(false);
      });
    } else {
      setInitialLoading(false);
    }
  }, [fetchStatus]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/waitlist/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          referredByCode: referredByCode || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success || !data.data) {
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }

      const entry = data.data;
      try {
        if (entry.referralCode) {
          localStorage.setItem(STORAGE_KEY, entry.referralCode);
        }
      } catch {}

      setStatus(entry as WaitlistStatus);
      setTimeout(() => statusRef.current?.focus(), 100);
    } catch (err) {
      console.error("Waitlist join error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  if (initialLoading) {
    return (
      <div className="w-full max-w-[480px]">
        <div className="bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] p-10 flex items-center justify-center min-h-[300px]">
          <Spinner />
        </div>
      </div>
    );
  }

  // Status view
  if (status) {
    const usedCount = status.inviteCodes?.filter((c) => c.used).length ?? 0;
    const totalCodes = status.inviteCodes?.length ?? 0;

    return (
      <div className="w-full max-w-[480px]">
        <div
          ref={statusRef}
          tabIndex={-1}
          className="bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] p-10 outline-none"
          role="status"
          aria-label={`You are number ${status.effectivePosition} on the waitlist`}
        >
          {/* Logo */}
          <div className="mb-8">
            <span className="font-semibold tracking-tight text-[19px] leading-none">Genie</span>
          </div>

          {/* Header */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <CheckIcon />
            </div>
            <h1 className="text-lg font-semibold text-[#111]">
              You&apos;re on the list
            </h1>
          </div>

          <p className="text-sm text-[#666] mb-8 leading-relaxed">
            Share your invite links below to move up the list. {usedCount} of {totalCodes} invites used.
          </p>

          {/* Invite codes */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-medium text-[#999] uppercase tracking-wider">
                Your invite links
              </label>
              <span className="text-xs text-[#bbb]">{totalCodes - usedCount} remaining</span>
            </div>

            <div className="border border-[#eee] rounded-lg px-3">
              {status.inviteCodes && status.inviteCodes.length > 0 ? (
                status.inviteCodes.map((invite, i) => (
                  <InviteCodeRow key={invite.code} invite={invite} index={i} />
                ))
              ) : (
                <p className="py-4 text-center text-sm text-[#999]">No invite codes available</p>
              )}
            </div>
          </div>

          {/* Caption */}
          <p className="text-xs text-[#999] text-center">
            Each accepted invite moves you 5 spots up the list.
          </p>
        </div>
      </div>
    );
  }

  // Join form
  return (
    <div className="w-full max-w-[480px]">
      <div className="bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] p-10">
        {/* Logo */}
        <div className="mb-8">
          <span className="font-semibold tracking-tight text-[19px] leading-none">Genie</span>
        </div>

        <h1 className="text-2xl font-semibold text-[#111] mb-3">
          Join the waitlist
        </h1>
        <p className="text-sm text-[#666] mb-8 leading-relaxed">
          Be among the first to experience the creative intelligent canvas.
          Every accepted invite moves you 5 spots closer to the front.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              placeholder="Enter your email"
              className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm text-[#111] placeholder:text-[#aaa] focus:outline-none focus:ring-2 focus:ring-[#111]/10 focus:border-[#111]/20 transition-colors"
              aria-label="Email address"
              autoComplete="email"
              disabled={loading}
            />
            <div aria-live="assertive">
              {error && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#111] text-white rounded-lg px-6 py-3 text-sm font-medium hover:bg-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#111]/50 flex items-center justify-center gap-2"
          >
            {loading && <Spinner />}
            {loading ? "Joining..." : "Join Waitlist"}
          </button>
        </form>
      </div>
    </div>
  );
}
